import { EntityLink as Link } from '../../navigation/navigation'
import { useListState } from '../../navigation/useListState'
import React, { useState, useEffect } from 'react'
import { useEntityNavigate as useNavigate } from '../../navigation/navigation'
import api from '../../services/api'
import { useAuth } from '../../contexts/AuthContext'
import { usePageHeader } from '../../contexts/PageHeaderContext'
import { Input, Select, Button, Pagination } from '../../components/ui'
import './Company.css'

function Objects() {
  const { filters, setFilters, pagination, setPagination } = useListState({
    filters: {
      client_id: '',
      type: '',
      search: ''
    },
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 1
    },
  })
  const navigate = useNavigate()
  const { user } = useAuth()
  const canCreate = user?.permissions?.['objects.create']
  const [objects, setObjects] = useState([])
  const [objectTypes, setObjectTypes] = useState([])
  const [clients, setClients] = useState([])
  const [loading, setLoading] = useState(true)
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768)

  const [suggestions, setSuggestions] = useState([])

  useEffect(() => {
    fetchClients()
  }, [])

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await api.get('/company/objects/types')
        if (response.data?.types) setObjectTypes(response.data.types)
      } catch (e) {
        console.error('Error fetching object types:', e)
      }
    }
    fetchTypes()
  }, [])

  const effectiveSearch = (filters.search || '').trim().length >= 3 ? (filters.search || '').trim() : ''
  useEffect(() => {
    fetchObjects()
  }, [filters.client_id, filters.type, pagination.page, effectiveSearch])

  const fetchSuggestions = React.useCallback(async (q) => {
    const query = (q || '').trim()
    if (query.length < 3) {
      setSuggestions([])
      return
    }
    try {
      const res = await api.get(`/company/objects/suggest?q=${encodeURIComponent(query)}&limit=10`)
      setSuggestions(res.data?.suggestions || [])
    } catch {
      setSuggestions([])
    }
  }, [])

  useEffect(() => {
    if (!filters.search || filters.search.trim().length < 3) {
      setSuggestions([])
      return
    }
    const t = setTimeout(() => fetchSuggestions(filters.search), 300)
    return () => clearTimeout(t)
  }, [filters.search, fetchSuggestions])

  const applySuggestion = (obj) => {
    handleFilterChange('search', obj.name || '')
    setSuggestions([])
  }

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const fetchClients = async () => {
    try {
      const response = await api.get('/company/clients?limit=1000')
      if (response.data && response.data.clients) {
        setClients(response.data.clients)
      }
    } catch (error) {
      console.error('Error fetching clients:', error)
    }
  }

  const fetchObjects = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filters.client_id) params.append('client_id', filters.client_id)
      if (filters.type) params.append('type', filters.type)
      if (filters.search && filters.search.trim().length >= 3) params.append('search', filters.search.trim())
      params.append('page', pagination.page.toString())
      params.append('limit', pagination.limit.toString())

      const response = await api.get(`/company/objects?${params}`)
      
      if (response.data) {
        if (response.data.objects) {
          setObjects(response.data.objects)
          setPagination(prev => ({
            ...prev,
            total: response.data.total || 0,
            pages: response.data.pages || 1
          }))
        } else {
          setObjects([])
        }
      } else {
        setObjects([])
      }
    } catch (error) {
      console.error('Error fetching objects:', error)
      console.error('Error details:', error.response?.data || error.message)
      setObjects([])
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (field, value) => {
    const hadEffectiveSearch = (filters.search || '').trim().length >= 3
    const hasEffectiveSearch = (value || '').trim().length >= 3
    const resetPage = field !== 'search' || hadEffectiveSearch !== hasEffectiveSearch
    setFilters(prev => ({ ...prev, [field]: value }))
    if (resetPage) setPagination(prev => ({ ...prev, page: 1 }))
  }

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }))
  }

  const getObjectStatusClass = (status) => {
    if (!status) return ''
    const s = String(status).toLowerCase().trim()
    if (s === 'в работе' || s === 'active') return 'object-status-in-progress'
    if (s === 'работы завершены' || s === 'завершен' || s === 'completed') return 'object-status-completed'
    return 'object-status-not-started'
  }

  usePageHeader({
    renderActions: () => canCreate ? (
      <Button variant="primary" onClick={() => navigate('/company/objects/new')}>+ Создать объект</Button>
    ) : null
  }, [canCreate])

  if (loading) {
    return <div className="loading">Загрузка...</div>
  }

  return (
    <div className="company-page">
      <div className="filters-section">
        <div className="filters">
          <div className="filter-group">
            <label>Клиент</label>
            <Select
              value={filters.client_id}
              onChange={(e) => handleFilterChange('client_id', e.target.value)}
              aria-label="Клиент"
            >
              <option value="">Все клиенты</option>
              {clients.map((client) => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </Select>
          </div>
          <div className="filter-group">
            <label>Тип объекта</label>
            <Select
              value={filters.type}
              onChange={(e) => handleFilterChange('type', e.target.value)}
              aria-label="Тип объекта"
            >
              <option value="">Все типы</option>
              {objectTypes.map((t) => (
                <option key={t} value={t}>{t}</option>
              ))}
            </Select>
          </div>
          <div className="filter-group filter-group-search">
            <label>Поиск</label>
            <Input
              type="text"
              placeholder="Поиск по названию или адресу"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
              onFocus={() => filters.search && filters.search.trim().length >= 3 && fetchSuggestions(filters.search)}
              onBlur={() => setTimeout(() => setSuggestions([]), 200)}
              aria-label="Поиск по названию или адресу"
              autoComplete="off"
            />
            {suggestions.length > 0 && (
              <ul className="search-suggestions" role="listbox">
                {suggestions.map((s) => (
                  <li
                    key={s.id}
                    role="option"
                    className="search-suggestion-item"
                    onMouseDown={() => applySuggestion(s)}
                  >
                    <span className="search-suggestion-name">{s.name || '-'}</span>
                    {s.address && <span className="search-suggestion-inn">{s.address}</span>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <div className="table-section">
        {objects.length === 0 ? (
          <div className="no-data">Объекты не найдены</div>
        ) : (
          <>
            {isMobile ? (
              <>
              <div className="list-cards">
                {objects.map((object) => (
                  <div
                    key={object.id}
                    className="list-card ui-card ui-card-hover"
                    onClick={() => navigate(`/object/${object.id}`)}
                  >
                    <div className="list-card-header">
                      <Link to={`/object/${object.id}`} className="list-card-title">{object.name || '-'}</Link>
                    </div>
                    <div className="list-card-body">
                      <div className="list-card-row">
                        <span className="list-card-label">Адрес:</span>
                        <span className="list-card-value">{object.address || '-'}</span>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Клиент:</span>
                        <Link className={`list-card-value${object.client_id ? ' clickable-link' : ''}`} to={object.client_id ? `/client/${object.client_id}` : undefined}
                        >
                          {object.client_name || '-'}
                        </Link>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Тип:</span>
                        <span className="list-card-value">{object.type || '-'}</span>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Договоров:</span>
                        <span className="list-card-value mono">{object.contracts_count ?? '-'}</span>
                      </div>
                      <div className="list-card-row">
                        <span className="list-card-label">Статус:</span>
                        <span className={`list-card-value object-status-badge ${getObjectStatusClass(object.status)}`}>
                          {object.status || '-'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
                {pagination.total > 0 && (
                  <div className="company-pagination-bar-wrap">
                    <Pagination
                      page={pagination.page}
                      pages={pagination.pages}
                      total={pagination.total}
                      pageSize={pagination.limit}
                      onPageChange={handlePageChange}
                    />
                  </div>
                )}
              </>
            ) : (
              <div className="company-table-frame">
              <div className="table-responsive">
                <table className="ui-table data-table">
                  <thead>
                    <tr>
                      <th>Название</th>
                      <th>Адрес</th>
                      <th>Клиент</th>
                      <th>Тип</th>
                      <th className="text-right">Договоров</th>
                      <th>Статус</th>
                    </tr>
                  </thead>
                  <tbody>
                    {objects.map((object) => (
                      <tr
                        key={object.id}
                        className="clickable-row"
                        onClick={() => navigate(`/object/${object.id}`)}
                      >
                        <td>
                          <Link to={`/object/${object.id}`} className="list-primary-name">{object.name || '-'}</Link>
                        </td>
                        <td>{object.address || '-'}</td>
                        <td
                          onClick={(e) => {
                            e.stopPropagation()
                            if (object.client_id) navigate(`/client/${object.client_id}`)
                          }}
                        >
                          {object.client_id ? (
                            <Link className="ui-table-entity-link" to={object.client_id ? `/client/${object.client_id}` : undefined}>{object.client_name || '-'}</Link>
                          ) : (
                            object.client_name || '-'
                          )}
                        </td>
                        <td>{object.type || '-'}</td>
                        <td className="text-right mono">{object.contracts_count || 0}</td>
                        <td>
                          <span className={`object-status-badge ${getObjectStatusClass(object.status)}`}>
                            {object.status || '-'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
                {pagination.total > 0 && (
                  <Pagination
                    page={pagination.page}
                    pages={pagination.pages}
                    total={pagination.total}
                    pageSize={pagination.limit}
                    onPageChange={handlePageChange}
                  />
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default Objects
